import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import type {
  Promotion,
  CreatePromotionRequest,
  UpdatePromotionRequest,
  PaginatedResponse,
  ValidatePromoCodeResponse,
} from "@/types";

export const PROMOTION_KEYS = {
  all: ["promotions"] as const,
  lists: () => [...PROMOTION_KEYS.all, "list"] as const,
  list: (filters?: Record<string, unknown>) =>
    [...PROMOTION_KEYS.lists(), filters] as const,
  detail: (id: string) => [...PROMOTION_KEYS.all, "detail", id] as const,
  activeHappyHours: () =>
    [...PROMOTION_KEYS.all, "active-happy-hours"] as const,
};

interface PromotionFilters {
  type?: "discount_code" | "happy_hour" | "buy_x_get_y";
  isActive?: boolean;
  page?: number;
  limit?: number;
  [key: string]: string | number | boolean | undefined;
}

export function usePromotions(filters?: PromotionFilters) {
  return useQuery({
    queryKey: PROMOTION_KEYS.list(filters),
    queryFn: () =>
      apiClient.get<PaginatedResponse<Promotion>>("/promotions", filters),
  });
}

export function usePromotion(id: string) {
  return useQuery({
    queryKey: PROMOTION_KEYS.detail(id),
    queryFn: () => apiClient.get<Promotion>(`/promotions/${id}`),
    enabled: !!id,
  });
}

export function useActiveHappyHours() {
  return useQuery({
    queryKey: PROMOTION_KEYS.activeHappyHours(),
    queryFn: () =>
      apiClient.get<{ data: Promotion[] }>("/promotions/active-happy-hours"),
    refetchInterval: 60000, // Check every minute
  });
}

export function useCreatePromotion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePromotionRequest) =>
      apiClient.post<Promotion>("/promotions", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROMOTION_KEYS.all });
    },
  });
}

export function useUpdatePromotion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePromotionRequest }) =>
      apiClient.patch<Promotion>(`/promotions/${id}`, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: PROMOTION_KEYS.all });
      queryClient.invalidateQueries({ queryKey: PROMOTION_KEYS.detail(id) });
    },
  });
}

export function useTogglePromotion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      apiClient.post<Promotion>(`/promotions/${id}/toggle`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROMOTION_KEYS.all });
    },
  });
}

export function useDeletePromotion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/promotions/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROMOTION_KEYS.all });
    },
  });
}

export function useValidatePromoCode() {
  return useMutation({
    mutationFn: ({ code, subtotal }: { code: string; subtotal: number }) =>
      apiClient.post<ValidatePromoCodeResponse>("/promotions/validate", {
        code,
        subtotal,
      }),
  });
}
